import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Plus, Filter, ChevronDown, Edit3, Trash2, ArrowRight, X, Phone, Mail, Users, UserPlus, MessageCircle } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import ConvertLeadModal from "../components/leads/ConvertLeadModal";

var PRODUCT_LABELS = { consorcio: "Consórcio", seguro: "Seguro", carta_contemplada: "Carta Contemplada" };
var PRODUCT_COLORS = { consorcio: "bg-[#F26A4B]/10 text-[#F26A4B]", seguro: "bg-[#3B82F6]/10 text-[#3B82F6]", carta_contemplada: "bg-emerald-100 text-emerald-700" };
var STATUS_LABELS = {
  qualificacao: "Qualificação", simulacao: "Simulação", reuniao: "Reunião",
  proposta: "Proposta", negociacao: "Negociação", fechado: "Fechado", perdido: "Perdido",
};
var STATUS_COLORS = {
  qualificacao: "bg-blue-100 text-blue-700", simulacao: "bg-amber-100 text-amber-700",
  reuniao: "bg-purple-100 text-purple-700", proposta: "bg-orange-100 text-orange-700",
  negociacao: "bg-cyan-100 text-cyan-700", fechado: "bg-emerald-100 text-emerald-700",
  perdido: "bg-red-100 text-red-700",
};
var STATUS_ORDER = ["qualificacao", "simulacao", "reuniao", "proposta", "negociacao", "fechado", "perdido"];
var PRODUCT_OPTIONS = [
  { value: "", label: "Todos os Produtos" },
  { value: "consorcio", label: "Consórcio" },
  { value: "seguro", label: "Seguro" },
  { value: "carta_contemplada", label: "Carta Contemplada" },
];
var STATUS_OPTIONS = [
  { value: "", label: "Todos os Status" },
  { value: "qualificacao", label: "Qualificação" },
  { value: "simulacao", label: "Simulação" },
  { value: "reuniao", label: "Reunião" },
  { value: "proposta", label: "Proposta" },
  { value: "negociacao", label: "Negociação" },
  { value: "fechado", label: "Fechado" },
  { value: "perdido", label: "Perdido" },
];
var SOURCE_OPTIONS = [
  { value: "", label: "Selecionar..." },
  { value: "indicacao", label: "Indicação" },
  { value: "site", label: "Site" },
  { value: "telefone", label: "Telefone" },
  { value: "rede_social", label: "Rede Social" },
  { value: "outro", label: "Outro" },
];

function formatValue(v) {
  if (v == null || v === 0) return "-";
  return "R$ " + v.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch(e) { return "-"; }
}

// ─── Lead Form Modal ──────────────────────────────────────────
function LeadFormModal({ lead, onClose, onSave, canAssignBroker, brokers }) {
  var isEdit = !!lead;
  var formState = useState({
    name: lead?.name || "", email: lead?.email || "", phone: lead?.phone || "",
    product_type: lead?.product_type || "consorcio", status: lead?.status || "qualificacao",
    value: lead?.value || "", description: lead?.description || "", source: lead?.source || "",
    referral: lead?.referral || "", assigned_broker_id: lead?.broker_id || "",
  });
  var form = formState[0];
  var setForm = formState[1];
  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  function updateField(field, value) {
    setForm(function(prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[field] = value; return n; });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      var payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        product_type: form.product_type,
        status: form.status,
        value: form.value ? parseFloat(form.value) : null,
        description: form.description.trim() || null,
        source: form.source || null,
        referral: form.referral.trim() || null,
      };
      if (canAssignBroker && form.assigned_broker_id) {
        payload.assigned_broker_id = form.assigned_broker_id;
      }
      if (isEdit) {
        await api.put("/leads/" + lead.id, payload);
      } else {
        await api.post("/leads", payload);
      }
      onSave();
    } catch (err) {
      var detail = err.response?.data?.detail || "Erro ao salvar lead";
      setError(typeof detail === "string" ? detail : "Erro ao salvar lead");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg" data-testid="lead-form-modal">
        <div className="bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-[#0A2F3D]">{isEdit ? "Editar Lead" : "Novo Lead"}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="lead-form-close"><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          {error && <div className="mx-5 mt-3 px-4 py-2.5 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">{error}</div>}
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Nome *</label>
              <input type="text" value={form.name} onChange={function(e) { updateField("name", e.target.value); }}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6]"
                placeholder="Nome completo do cliente" required data-testid="lead-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Email</label>
                <input type="email" value={form.email} onChange={function(e) { updateField("email", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6]"
                  placeholder="email@exemplo.com" data-testid="lead-form-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Telefone</label>
                <input type="tel" value={form.phone} onChange={function(e) { updateField("phone", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6]"
                  placeholder="(11) 99999-9999" data-testid="lead-form-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Produto</label>
                <select value={form.product_type} onChange={function(e) { updateField("product_type", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6] bg-white"
                  data-testid="lead-form-product">
                  <option value="consorcio">Consórcio</option>
                  <option value="seguro">Seguro</option>
                  <option value="carta_contemplada">Carta Contemplada</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
                <select value={form.status} onChange={function(e) { updateField("status", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6] bg-white"
                  data-testid="lead-form-status">
                  {STATUS_OPTIONS.filter(function(s) { return s.value; }).map(function(s) {
                    return <option key={s.value} value={s.value}>{s.label}</option>;
                  })}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Origem</label>
                <select value={form.source} onChange={function(e) { updateField("source", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6] bg-white"
                  data-testid="lead-form-source">
                  {SOURCE_OPTIONS.map(function(s) { return <option key={s.value} value={s.value}>{s.label}</option>; })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Indicação</label>
                <input type="text" value={form.referral} onChange={function(e) { updateField("referral", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6]"
                  placeholder="Quem indicou?" data-testid="lead-form-referral" />
              </div>
            </div>
            {canAssignBroker && brokers && brokers.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Corretor Responsavel</label>
                <select value={form.assigned_broker_id} onChange={function(e) { updateField("assigned_broker_id", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6] bg-white"
                  data-testid="lead-form-broker">
                  <option value="">Eu mesmo</option>
                  {brokers.map(function(b) { return <option key={b.id} value={b.id}>{b.name}</option>; })}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Observação</label>
              <textarea value={form.description} onChange={function(e) { updateField("description", e.target.value); }}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 focus:border-[#3B82F6] resize-none"
                rows="2" placeholder="Detalhes sobre o lead..." data-testid="lead-form-description" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button type="submit" disabled={loading}
                className="flex-1 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] transition-colors disabled:opacity-50"
                data-testid="lead-form-submit">
                {loading ? "Salvando..." : isEdit ? "Salvar Alterações" : "Cadastrar Lead"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ─── Stage Change Dropdown ───────────────────────────────────
function StageDropdown({ lead, onStageChange }) {
  var openState = useState(false);
  var isOpen = openState[0];
  var setIsOpen = openState[1];
  var currentIdx = STATUS_ORDER.indexOf(lead.status);

  function handleChange(newStatus) {
    setIsOpen(false);
    if (newStatus !== lead.status) onStageChange(lead.id, newStatus);
  }

  return (
    <div className="relative">
      <button onClick={function(e) { e.stopPropagation(); setIsOpen(!isOpen); }}
        className={"inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer " + (STATUS_COLORS[lead.status] || "bg-gray-100 text-gray-700")}
        data-testid={"lead-status-" + lead.id}>
        {STATUS_LABELS[lead.status] || lead.status}
        <ChevronDown className="w-3 h-3" />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={function() { setIsOpen(false); }} />
          <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-40 w-44" data-testid={"stage-dropdown-" + lead.id}>
            {STATUS_ORDER.map(function(s) {
              var isActive = s === lead.status;
              return (
                <button key={s} onClick={function(e) { e.stopPropagation(); handleChange(s); }}
                  className={"w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 " + (isActive ? "font-semibold text-[#0A2F3D]" : "text-gray-600")}>
                  <span className={"w-2 h-2 rounded-full flex-shrink-0 " + (STATUS_COLORS[s] || "bg-gray-300").split(" ")[0]} />
                  {STATUS_LABELS[s]}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Delete Confirm Modal ────────────────────────────────────
function DeleteConfirmModal({ lead, onClose, onConfirm }) {
  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  async function handleDelete() {
    setLoading(true);
    await onConfirm(lead.id);
    setLoading(false);
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm" data-testid="delete-confirm-modal">
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <h3 className="text-lg font-bold text-[#0A2F3D] mb-2">Excluir Lead</h3>
          <p className="text-sm text-gray-500 mb-5">Tem certeza que deseja excluir <strong>{lead.name}</strong>? Esta ação não pode ser desfeita.</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button onClick={handleDelete} disabled={loading}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-50"
              data-testid="delete-confirm-button">
              {loading ? "Excluindo..." : "Excluir"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main Leads Page ────────────────────────────────────────
export default function LeadsPage() {
  var auth = useAuth();
  var user = auth.user;
  var isAdmin = user?.role === "admin";
  var isTeamLeader = user?.role === "team_leader";
  var isAssistant = user?.role === "assistant";
  var canAssignBroker = isAdmin || isTeamLeader || isAssistant;
  var showBrokerCol = isAdmin || isTeamLeader || isAssistant;

  var leadsState = useState([]);
  var leads = leadsState[0];
  var setLeads = leadsState[1];
  var totalState = useState(0);
  var total = totalState[0];
  var setTotal = totalState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var pageState = useState(1);
  var page = pageState[0];
  var setPage = pageState[1];

  var searchState = useState("");
  var search = searchState[0];
  var setSearch = searchState[1];
  var filterProductState = useState("");
  var filterProduct = filterProductState[0];
  var setFilterProduct = filterProductState[1];

  var searchParamsResult = useSearchParams();
  var searchParams = searchParamsResult[0];

  useEffect(function() {
    var p = searchParams.get("produto");
    if (p) setFilterProduct(p);
  }, [searchParams]);
  var filterStatusState = useState("");
  var filterStatus = filterStatusState[0];
  var setFilterStatus = filterStatusState[1];

  var showFormState = useState(false);
  var showForm = showFormState[0];
  var setShowForm = showFormState[1];
  var editingLeadState = useState(null);
  var editingLead = editingLeadState[0];
  var setEditingLead = editingLeadState[1];
  var deletingLeadState = useState(null);
  var deletingLead = deletingLeadState[0];
  var setDeletingLead = deletingLeadState[1];
  var convertingLeadState = useState(null);
  var convertingLead = convertingLeadState[0];
  var setConvertingLead = convertingLeadState[1];

  var brokersState = useState([]);
  var brokers = brokersState[0];

  useEffect(function() {
    if (canAssignBroker) {
      api.get("/dashboard/team").then(function(res) {
        var b = res.data?.brokers || [];
        var a = res.data?.assistants || [];
        brokersState[1](b.concat(a));
      }).catch(function() {});
    }
  }, [canAssignBroker]);

  var fetchLeads = useCallback(async function() {
    setLoading(true);
    try {
      var params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterProduct) params.append("product_type", filterProduct);
      if (filterStatus) params.append("status", filterStatus);
      params.append("page", page);
      params.append("limit", "20");
      var res = await api.get("/leads?" + params.toString());
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Erro ao carregar leads:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterProduct, filterStatus, page]);

  useEffect(function() { fetchLeads(); }, [fetchLeads]);

  useEffect(function() {
    function onRefresh() { fetchLeads(); }
    window.addEventListener("dashboard:refresh", onRefresh);
    return function() { window.removeEventListener("dashboard:refresh", onRefresh); };
  }, [fetchLeads]);

  async function handleStageChange(leadId, newStatus) {
    if (newStatus === "fechado") {
      var targetLead = leads.find(function(l) { return l.id === leadId; });
      if (targetLead && !targetLead.converted_client_id) {
        setConvertingLead(targetLead);
        return;
      }
    }
    try {
      await api.put("/leads/" + leadId + "/stage", { status: newStatus });
      fetchLeads();
    } catch (err) {
      console.error("Erro ao alterar status:", err);
    }
  }

  async function handleDelete(leadId) {
    try {
      await api.delete("/leads/" + leadId);
      setDeletingLead(null);
      fetchLeads();
    } catch (err) {
      console.error("Erro ao excluir lead:", err);
    }
  }

  function handleSave() {
    setShowForm(false);
    setEditingLead(null);
    fetchLeads();
  }

  function openEdit(lead) {
    setEditingLead(lead);
    setShowForm(true);
  }

  function openCreate() {
    setEditingLead(null);
    setShowForm(true);
  }

  var totalPages = Math.ceil(total / 20);

  var statusCounts = {};
  STATUS_ORDER.forEach(function(s) { statusCounts[s] = 0; });
  leads.forEach(function(l) { if (statusCounts[l.status] != null) statusCounts[l.status]++; });

  return (
    <AppShell activePage="leads" title="Leads">
      <div data-testid="leads-page">
        {showForm && <LeadFormModal lead={editingLead} onClose={function() { setShowForm(false); setEditingLead(null); }} onSave={handleSave} canAssignBroker={canAssignBroker} brokers={brokers} />}
        {deletingLead && <DeleteConfirmModal lead={deletingLead} onClose={function() { setDeletingLead(null); }} onConfirm={handleDelete} />}
        {convertingLead && <ConvertLeadModal lead={convertingLead} onClose={function() { setConvertingLead(null); }} onConverted={function() { setConvertingLead(null); fetchLeads(); }} />}

        {/* Pipeline Summary */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1" data-testid="pipeline-summary">
          {STATUS_ORDER.filter(function(s) { return s !== "perdido"; }).map(function(s) {
            var isActive = filterStatus === s;
            var baseColor = STATUS_COLORS[s] || "bg-gray-100 text-gray-700";
            return (
              <button key={s} onClick={function() { setFilterStatus(isActive ? "" : s); setPage(1); }}
                className={"flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border transition-all whitespace-nowrap " + (isActive ? baseColor + " border-current shadow-sm" : "bg-white border-gray-100 text-gray-500 hover:border-gray-200")}
                data-testid={"pipeline-" + s}>
                {STATUS_LABELS[s]}
              </button>
            );
          })}
        </div>

        {/* Search + Filters + New Button */}
        <div className="bg-white rounded-xl lg:rounded-2xl p-4 shadow-sm mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={search}
                onChange={function(e) { setSearch(e.target.value); setPage(1); }}
                placeholder="Buscar por nome, email ou telefone..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]"
                data-testid="leads-search-input" />
            </div>
            <div className="flex gap-2">
              <select value={filterProduct} onChange={function(e) { setFilterProduct(e.target.value); setPage(1); }}
                className="px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20 bg-white"
                data-testid="leads-filter-product">
                {PRODUCT_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
              </select>
              <select value={filterStatus} onChange={function(e) { setFilterStatus(e.target.value); setPage(1); }}
                className="px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20 bg-white"
                data-testid="leads-filter-status">
                {STATUS_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
              </select>
              <button onClick={openCreate}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] transition-colors whitespace-nowrap"
                data-testid="leads-new-button">
                <Plus className="w-4 h-4" /><span className="hidden sm:inline">Novo Lead</span>
              </button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
            <span data-testid="leads-total-count">{total} lead{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}</span>
            {(search || filterProduct || filterStatus) && (
              <button onClick={function() { setSearch(""); setFilterProduct(""); setFilterStatus(""); setPage(1); }}
                className="text-[#3B82F6] hover:underline" data-testid="leads-clear-filters">Limpar filtros</button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden" data-testid="leads-table-container">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Nenhum lead encontrado</p>
              <p className="text-gray-400 text-sm mt-1">Cadastre seu primeiro lead clicando no botão acima</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Lead</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Contato</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                      {isAdmin && <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Indicação</th>}
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      {showBrokerCol && <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Corretor</th>}
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(function(lead) {
                      return (
                        <tr key={lead.id} className="border-b border-gray-50 hover:bg-[#3B82F6]/[0.02] transition-colors" data-testid={"lead-row-" + lead.id}>
                          <td className="py-3 px-4">
                            <p className="text-sm font-medium text-[#0A2F3D]">{lead.name}</p>
                            {lead.source && <p className="text-xs text-gray-400 mt-0.5">{SOURCE_OPTIONS.find(function(s){ return s.value === lead.source; })?.label || lead.source}</p>}
                          </td>
                          <td className="py-3 px-4">
                            {lead.email && <div className="flex items-center gap-1.5 text-xs text-gray-500"><Mail className="w-3 h-3" />{lead.email}</div>}
                            {lead.phone && <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5"><Phone className="w-3 h-3" />{lead.phone}<a href={"https://wa.me/55" + lead.phone.replace(/\D/g, "")} target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-emerald-600" data-testid={"lead-wpp-" + lead.id} title="Abrir WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></a></div>}
                            {!lead.email && !lead.phone && <span className="text-xs text-gray-300">-</span>}
                          </td>
                          <td className="py-3 px-4">
                            <span className={"px-2 py-1 text-xs font-medium rounded-full " + (PRODUCT_COLORS[lead.product_type] || "bg-gray-100 text-gray-700")}>
                              {PRODUCT_LABELS[lead.product_type] || lead.product_type}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-[#0A2F3D]">{formatValue(lead.value)}</td>
                          {isAdmin && <td className="py-3 px-4 text-xs text-gray-500">{lead.referral || "-"}</td>}
                          <td className="py-3 px-4">
                            <StageDropdown lead={lead} onStageChange={handleStageChange} />
                          </td>
                          {showBrokerCol && <td className="py-3 px-4 text-xs text-gray-500">{lead.broker_name || "-"}</td>}
                          <td className="py-3 px-4 text-xs text-gray-400">{formatDate(lead.created_at)}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-1">
                              {lead.status === "fechado" && !lead.converted_client_id && (
                                <button onClick={function() { setConvertingLead(lead); }}
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors mr-1"
                                  data-testid={"lead-convert-" + lead.id} title="Cadastrar Cliente">
                                  <UserPlus className="w-3.5 h-3.5" />Cadastrar
                                </button>
                              )}
                              {lead.converted_client_id && (
                                <span className="text-xs text-emerald-600 font-medium mr-1" data-testid={"lead-converted-" + lead.id}>Convertido</span>
                              )}
                              <button onClick={function() { openEdit(lead); }}
                                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-[#3B82F6] transition-colors"
                                data-testid={"lead-edit-" + lead.id} title="Editar">
                                <Edit3 className="w-4 h-4" />
                              </button>
                              {isAdmin && (
                                <button onClick={function() { setDeletingLead(lead); }}
                                  className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                                  data-testid={"lead-delete-" + lead.id} title="Excluir">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-50">
                {leads.map(function(lead) {
                  return (
                    <div key={lead.id} className="p-4" data-testid={"lead-card-" + lead.id}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-[#0A2F3D]">{lead.name}</p>
                          <span className={"px-2 py-0.5 text-xs font-medium rounded-full mt-1 inline-block " + (PRODUCT_COLORS[lead.product_type] || "")}>
                            {PRODUCT_LABELS[lead.product_type]}
                          </span>
                        </div>
                        <StageDropdown lead={lead} onStageChange={handleStageChange} />
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}<a href={"https://wa.me/55" + lead.phone.replace(/\D/g, "")} target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-emerald-600"><MessageCircle className="w-3.5 h-3.5" /></a></span>}
                          <span>{formatValue(lead.value)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={function() { openEdit(lead); }} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <button onClick={function() { setDeletingLead(lead); }} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Página {page} de {totalPages}</p>
                  <div className="flex gap-1">
                    <button disabled={page <= 1} onClick={function() { setPage(page - 1); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid="leads-prev-page">Anterior</button>
                    <button disabled={page >= totalPages} onClick={function() { setPage(page + 1); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid="leads-next-page">Próxima</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
